import { useEffect, useState } from "react";
import { getDataCounting } from './../../redux/slices/dataCountSlice';
import { useDispatch, useSelector } from "react-redux";

// SVG Icons
const PeopleIcon = ({ size = 20, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const CategoryIcon = ({ size = 20, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const ArticleIcon = ({ size = 20, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM16 18H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);

const HeartIcon = ({ size = 20, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const BgPattern = ({ color }) => (
  <div
    style={{
      position: "absolute",
      right: 0,
      top: "-30%",
      opacity: 0.1,
      transform: "translateX(20%) translateY(20%)",
      zIndex: 0,
    }}
  >
    <svg width="150" height="150" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="40" fill={color} />
      <path
        d="M30 70c20-20 40-20 60 0"
        stroke={color}
        strokeWidth="8"
        fill="none"
      />
    </svg>
  </div>
);

const StatCard = ({
  title,
  count,
  icon: Icon,
  color,
  description,
  bgGradient,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardStyle = {
    background: bgGradient,
    backdropFilter: "blur(10px)",
    border: `1px solid ${color}40`,
    borderRadius: "12px",
    transition: "all 0.3s ease",
    transform: isHovered ? "translateY(-6px)" : "translateY(0)",
    boxShadow: isHovered
      ? `0 6px 18px -6px ${color}40`
      : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    position: "relative",
    overflow: "hidden",
    height: "100%",
    minHeight: "100px",
  };

  const iconContainerStyle = {
    backgroundColor: color,
    borderRadius: "10px",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 6px 14px -8px ${color}`,
    width: "fit-content",
  };

  const countStyle = {
    fontWeight: 700,
    color: color,
    fontSize: "1.2rem",
    margin: 0,
  };

  return (
    <div
      className="h-100 p-3"
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="d-flex align-items-center justify-content-between mb-2"
        style={{ zIndex: 1 }}
      >
        <div style={iconContainerStyle}>
          <Icon size={20} color="white" />
        </div>
        <h3 style={countStyle}>{count || "0"}</h3>
      </div>

      <div style={{ zIndex: 1 }}>
        <h6 className="fw-semibold text-dark mb-1" style={{ fontSize: "1rem" }}>
          {title}
        </h6>
        <p
          className="text-muted mb-0 d-none d-sm-block"
          style={{ fontSize: "0.85rem" }}
        >
          {description}
        </p>
      </div>

      <BgPattern color={color} />
    </div>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { dataCount } = useSelector((state) => state.dataCount);

  useEffect(() => {
    dispatch(getDataCounting());
  }, [dispatch]);

  const cardData = [
    {
      title: "Team Members",
      count: dataCount?.teamMembers || 0,
      icon: PeopleIcon,
      color: "#1976d2",
      bgGradient: "linear-gradient(135deg, #1976d215, #ffffff)",
      description: "Active members in foundation team",
    },
    {
      title: "Organ Categories",
      count: dataCount?.organCategories || 0,
      icon: CategoryIcon,
      color: "#2e7d32",
      bgGradient: "linear-gradient(135deg, #2e7d3215, #ffffff)",
      description: "Organ Donation Categories",
    },
    {
      title: "Total Blogs",
      count: dataCount?.blogs || 0,
      icon: ArticleIcon,
      color: "#ed6c02",
      bgGradient: "linear-gradient(135deg, #ed6c0215, #ffffff)",
      description: "Published articles and stories",
    },
    {
      title: "Donor Reviews",
      count: dataCount?.donorReviews || 0,
      icon: HeartIcon,
      color: "#d32f2f",
      bgGradient: "linear-gradient(135deg, #d32f2f15, #ffffff)",
      description: "Testimonials from our donors",
    },
  ];

  const containerStyle = {
    padding: "2rem",
    background: "linear-gradient(145deg, #f5f5f5, #ffffff)",
    minHeight: "100vh",
    borderRadius: "8px",
  };

  const headerStyle = {
    fontWeight: 800,
    marginBottom: "2rem",
    background: "linear-gradient(45deg, #1976d2, #115293)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.1))",
  };

  return (
    <>
      {/* Bootstrap CSS */}
      <link
        href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css"
        rel="stylesheet"
      />

      <div style={containerStyle}>
        <h2 style={headerStyle} className="text-center text-sm-start">
          Dashboard Overview
        </h2>

        {/* Always 4 columns in single row */}
        <div className="row g-2 g-sm-3 g-md-4">
          {cardData.map((card, index) => (
            <div key={index} className="col-3">
              <StatCard
                title={card.title}
                count={card.count}
                icon={card.icon}
                color={card.color}
                bgGradient={card.bgGradient}
                description={card.description}
              />
            </div>
          ))}
        </div>

        {/* Additional Info for small screens */}
        <div className="mt-4 d-block d-sm-none">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="card-title fw-semibold">Card Details:</h6>
              <div className="row g-2">
                {cardData.map((card, index) => (
                  <div key={index} className="col-12">
                    <div className="d-flex justify-content-between align-items-center py-1">
                      <small className="text-muted">{card.title}:</small>
                      <small className="fw-medium">{card.description}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
